import { z } from 'zod';

import { contactSubjectSchema, directionReasonSchema } from './public';

/**
 * Mensagens do formulario de contacto no back-office. `contacts.manage` ve
 * todas menos as da Direcao; `contacts.direcao` (papel Direcao, F-018) ve so
 * essas. So o host de gestao encaminha `/api/site/`.
 *
 *   - `GET    /api/site/contacts`              lista (filtros `status`, `subject`);
 *   - `GET    /api/site/contacts/:id`          a mensagem inteira;
 *   - `PUT    /api/site/contacts/:id/status`   nova, tratada ou arquivada;
 *   - `POST   /api/site/contacts/:id/resend`   volta a enviar o email que falhou;
 *   - `POST   /api/site/contacts/:id/notes`    junta uma nota interna (so a equipa ve);
 *   - `DELETE /api/site/contacts/:id`          apaga de vez.
 */

export const contactStatusSchema = z.enum(['new', 'handled', 'archived']);
export type ContactStatus = z.infer<typeof contactStatusSchema>;

export const CONTACT_STATUS_LABELS: Record<ContactStatus, string> = {
  new: 'Nova',
  handled: 'Tratada',
  archived: 'Arquivada',
};

export const contactListQuerySchema = z.object({
  status: contactStatusSchema.optional(),
  subject: contactSubjectSchema.optional(),
});
export type ContactListQuery = z.infer<typeof contactListQuerySchema>;

export const adminContactSummarySchema = z.object({
  id: z.string().uuid(),
  subject: contactSubjectSchema,
  status: contactStatusSchema,
  name: z.string(),
  email: z.string(),
  createdAt: z.string(),
  /** `null` enquanto o email para CONTACT_TO nao saiu. */
  mailedAt: z.string().nullable(),
  statusChangedAt: z.string().nullable(),
  statusChangedBy: z.string().nullable(),
  /** Notas internas da equipa sobre esta mensagem. */
  noteCount: z.number().int().nonnegative(),
  /** Respostas enviadas pelo back-office a quem escreveu. */
  replyCount: z.number().int().nonnegative(),
  /** So nas da Direcao: a conta de quem escreveu e porque podia. */
  account: z
    .object({ id: z.string().uuid(), login: z.string(), reasons: z.array(directionReasonSchema) })
    .nullable(),
});
export type AdminContactSummary = z.infer<typeof adminContactSummarySchema>;

/** Nota interna: so a equipa a ve, nunca e enviada a quem escreveu. */
export const contactNoteSchema = z.object({
  id: z.string().uuid(),
  text: z.string(),
  createdAt: z.string(),
  createdBy: z.string(),
});
export type ContactNote = z.infer<typeof contactNoteSchema>;

/** Resposta enviada por email a quem escreveu, a partir do back-office. */
export const contactReplySchema = z.object({
  id: z.string().uuid(),
  text: z.string(),
  createdAt: z.string(),
  createdBy: z.string(),
  /** `null` se o email nao saiu: pode reenviar-se. */
  mailedAt: z.string().nullable(),
});
export type ContactReply = z.infer<typeof contactReplySchema>;

export const adminContactSchema = adminContactSummarySchema.extend({
  message: z.string(),
  /** Mais antigas primeiro, como uma conversa. */
  notes: z.array(contactNoteSchema),
  /** Mais antigas primeiro. */
  replies: z.array(contactReplySchema),
});
export type AdminContact = z.infer<typeof adminContactSchema>;

export const contactStatusRequestSchema = z.object({ status: contactStatusSchema });
export type ContactStatusRequest = z.infer<typeof contactStatusRequestSchema>;

export const CONTACT_NOTE_MAX = 2000;
export const contactNoteRequestSchema = z.object({
  text: z.string().trim().min(1, 'Escreva a nota.').max(CONTACT_NOTE_MAX),
});
export type ContactNoteRequest = z.infer<typeof contactNoteRequestSchema>;

export const CONTACT_REPLY_MAX = 5000;
export const contactReplyRequestSchema = z.object({
  text: z.string().trim().min(1, 'Escreva a resposta.').max(CONTACT_REPLY_MAX),
});
export type ContactReplyRequest = z.infer<typeof contactReplyRequestSchema>;
